import React, { useEffect, useState, useCallback } from 'react';
import { Card, Tag, Spin, message, Empty, Statistic, Alert } from 'antd';
import ResponsiveTable from '../../components/common/ResponsiveTable';
import { GiftOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import ChildSelector from '../../components/parent/ChildSelector';

const PAYMENT_METHOD_LABELS = {
  cash: 'Nakit', nakit: 'Nakit',
  transfer: 'Havale/EFT', bank_transfer: 'Havale/EFT', havale: 'Havale/EFT',
  card: 'Kart', credit_card: 'Kart', kredi_karti: 'Kart',
  online: 'Online',
  other: 'Diğer', diger: 'Diğer',
};
const fmtMethod = (v) => PAYMENT_METHOD_LABELS[v?.toLowerCase?.()] || v || '—';

// Mobil uygulamadaki gibi: cocuk secici + kisi bazinda borclandirilan/odenen/
// bakiye ozeti + borclandirilan dersler dokumu + odeme gecmisi. Eskiden
// sadece tum cocuklarin odemeleri karisik tek bir tabloda, "Borclandirilan
// Dersler" dokumu hic yoktu (#83).
const ParentPayments = () => {
  const [dashLoading, setDashLoading] = useState(true);
  const [children, setChildren]       = useState([]);
  const [selectedId, setSelectedId]   = useState(null);

  const [childLoading, setChildLoading] = useState(false);
  const [childData, setChildData]       = useState({ charged: 0, paid: 0, debt: 0, charges: [] });

  const [payments, setPayments] = useState([]);
  const [payLoading, setPayLoading] = useState(false);
  const [page, setPage]         = useState(1);
  const [total, setTotal]       = useState(0);
  const [notice, setNotice]     = useState(null);

  useEffect(() => {
    setDashLoading(true);
    api.get('/parent/dashboard')
      .then(res => {
        const list = res.data.children || [];
        setChildren(list);
        // `debt_summary` BURADA KULLANILMIYOR — her çocuğun kendi özeti
        // `parent/charges/{id}` ile ayrıca çekiliyor (childData). Toplam
        // ise 15.10.2026'dan beri veli PANOSUNDA gösteriliyor
        // (ParentDashboard "Hesap Durumu"). Burada bir `grandTotals`
        // state'i vardı ama hiçbir yerde çizilmiyordu — kaldırıldı.
        if (list.length > 0) setSelectedId(list[0].id);
      })
      .catch(() => message.error('Ödeme bilgileri yüklenemedi.'))
      .finally(() => setDashLoading(false));
  }, []);

  const fetchChildCharges = useCallback((studentId) => {
    setChildLoading(true);
    api.get(`/parent/charges/${studentId}`)
      .then(res => {
        setChildData({
          charged: Number(res.data.total_charged) || 0,
          paid:    Number(res.data.total_paid)    || 0,
          debt:    Number(res.data.total_debt)    || 0,
          charges: res.data.charges || [],
        });
      })
      .catch(() => setChildData({ charged: 0, paid: 0, debt: 0, charges: [] }))
      .finally(() => setChildLoading(false));
  }, []);

  const fetchPayments = useCallback((studentId, p = 1) => {
    setPayLoading(true);
    api.get('/parent/payments', { params: { page: p, per_page: 10, student_id: studentId || undefined } })
      .then(res => {
        setPayments(res.data.data || []);
        setTotal(res.data.meta?.total || res.data.total || 0);
        // Metin sunucudan geliyor (duzenlenebilir sablon) - burada sabit
        // yazilmis bir karsiligi YOK, bos gelirse kutu hic cizilmiyor.
        setNotice(res.data.notice || null);
      })
      .catch(() => message.error('Ödeme geçmişi yüklenemedi.'))
      .finally(() => setPayLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setPage(1);
    fetchChildCharges(selectedId);
    fetchPayments(selectedId, 1);
  }, [selectedId, fetchChildCharges, fetchPayments]);

  const selectedChild = children.find(c => c.id === selectedId);
  const hasCredit = childData.paid > childData.charged;

  const chargeColumns = [
    {
      title: 'Tarih',
      dataIndex: 'date',
      render: v => v ? dayjs(v).format('DD.MM.YYYY') : '—',
    },
    {
      title: 'Saat / Derslik',
      key: 'slot',
      render: (_, r) => (
        <span className="text-sm text-gray-500">
          {r.start_time ? `${r.start_time.slice(0, 5)}${r.end_time ? ` – ${r.end_time.slice(0, 5)}` : ''}` : ''}
          {r.classroom ? ` · ${r.classroom}` : ''}
        </span>
      ),
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      align: 'right',
      render: v => <span className="font-semibold text-orange-500">{v ? `${v} ₺` : '—'}</span>,
    },
  ];

  const paymentColumns = [
    {
      title: 'Tarih',
      dataIndex: 'paid_at',
      render: v => v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      render: v => <span className="font-semibold text-green-600">{v ? `${v} ₺` : '—'}</span>,
    },
    {
      title: 'Yöntem',
      dataIndex: 'payment_method',
      render: v => <Tag color="blue">{fmtMethod(v)}</Tag>,
    },
    {
      title: 'Alan',
      key: 'received_by',
      render: (_, r) => (r.receivedBy || r.received_by) ? `${(r.receivedBy || r.received_by).name} ${(r.receivedBy || r.received_by).surname}` : '—',
    },
    {
      title: 'Durum',
      key: 'status',
      render: () => <Tag color="success">Ödendi</Tag>,
    },
  ];

  if (dashLoading) {
    return <div className="flex justify-center py-16"><Spin size="large" /></div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-0">Ödemeler</h2>

      {/*
        GEÇİŞ TARİHİ NOTU — rakamların ÜSTÜNDE durmalı.

        Kurum 29.06.2026'da bu yazılıma geçti, öncesi Excel'de kaldı. Veli
        kendi yaptığı ödemeyi ekranda bulamayıp "ben 2800 ödedim" diyor.
        Not, rakamı gördükten SONRA değil görmeden ÖNCE okunmalı; aşağı
        alınırsa işlevini kaybeder.

        Metin sunucudan geliyor ve şablon pasife alınınca `notice` boş
        döner — o zaman kutu hiç çizilmez, burada silinecek kod kalmaz.
      */}
      {notice && (
        <Alert type="info" showIcon message={notice} />
      )}

      <ChildSelector children={children} value={selectedId} onChange={setSelectedId} />

      {childLoading ? (
        <div className="flex justify-center py-8"><Spin /></div>
      ) : (
        <>
          {/*
            VELİYE YALNIZCA FARK GÖSTERİLİYOR (2026-08-26).

            Önce üç kart vardı: "Toplam Ders Tutarı", "Yapılan Ödeme" ve
            kalan. Kurumun isteği: veli dönem boyunca ödediği toplamı görünce
            "bu kadar para mı harcadık" diye moralinin bozulduğu oluyor.
            Artık yalnızca ödenmesi gereken (ya da lehte bakiye) duruyor.

            YALNIZCA VELİ EKRANINDA — personel tarafındaki ödeme ekranları
            (Öğrenci detayı > Borç & Ödeme, Finans raporları) dokunulmadı,
            oralarda toplamlar operasyon için gerekli.

            Ödeme geçmişi ve borçlandırılan dersler listeleri KALDI: veli
            "ödemem işlendi mi" diye bakabilmeli. Kaldırılan şey tek bir
            yekûn rakamı, kayıtların kendisi değil.
          */}
          <div className="grid grid-cols-1 gap-3">
            {hasCredit ? (
              <Card size="small">
                <Statistic
                  title="Bakiye"
                  value={childData.paid - childData.charged}
                  suffix="₺"
                  prefix={<GiftOutlined />}
                  valueStyle={{ color: '#1677ff' }}
                />
              </Card>
            ) : (
              <Card size="small">
                <Statistic
                  title="Kalan Tutar"
                  value={childData.debt}
                  suffix="₺"
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            )}
          </div>

          {hasCredit && (
            <Alert
              type="info"
              showIcon
              message={`${selectedChild?.name?.split(' ')[0] || 'Öğrenci'} için ${childData.paid - childData.charged} ₺ bakiyeniz bulunmaktadır.`}
            />
          )}

          <Card size="small" title="Borçlandırılan Dersler">
            <ResponsiveTable
              size="small"
              scroll={{ x: 'max-content' }}
              dataSource={childData.charges}
              columns={chargeColumns}
              rowKey="id"
              pagination={false}
              locale={{ emptyText: <Empty description="Borçlandırılan ders bulunamadı" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          </Card>
        </>
      )}

      <Card size="small" title="Ödeme Geçmişi">
        <ResponsiveTable
          size="small"
          scroll={{ x: 'max-content' }}
          loading={payLoading}
          dataSource={payments}
          columns={paymentColumns}
          rowKey="id"
          locale={{ emptyText: <Empty description="Ödeme kaydı bulunamadı" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          pagination={{
            current: page,
            pageSize: 10,
            total,
            onChange: p => { setPage(p); fetchPayments(selectedId, p); },
            showTotal: t => `Toplam ${t} ödeme`,
          }}
        />
      </Card>
    </div>
  );
};

export default ParentPayments;
